import type { LiveComposerEvent, LiveComposerPedalEvent } from '../../CreateMusicXml';

export type PlaybackInstruction =
  | { id: string; atDivisions: number; type: 'pedal'; pedalDown: boolean }
  | { id: string; atDivisions: number; type: 'end' }
  | {
      id: string;
      atDivisions: number;
      type: 'note-on' | 'note-off';
      midiNotes: number[];
      source: LiveComposerEvent['source'];
      velocities?: Record<number, number>;
    };

/** Seeking and looping restore notes crossing the start and pedal state at the boundary. */
export function buildPlaybackWindow(
  events: LiveComposerEvent[],
  pedals: LiveComposerPedalEvent[],
  start: number,
  end?: number,
): PlaybackInstruction[] {
  const result: PlaybackInstruction[] = [];
  const orderedPedals = [...pedals].sort((a, b) => a.positionDivisions - b.positionDivisions);
  let pedalDown = false;
  for (const pedal of orderedPedals) {
    if (pedal.positionDivisions <= start) pedalDown = pedal.pedalDown;
  }
  result.push({ id: 'boundary:pedal', type: 'pedal', atDivisions: start, pedalDown });
  for (const event of events) {
    const onset = event.startDivisions ?? 0;
    const release = onset + event.durationDivisions;
    const pedalAtRelease = orderedPedals
      .filter((pedal) => pedal.positionDivisions <= release)
      .at(-1)?.pedalDown;
    const sustainedAtStart =
      release <= start &&
      pedalAtRelease &&
      !orderedPedals.some(
        (pedal) =>
          !pedal.pedalDown && pedal.positionDivisions > release && pedal.positionDivisions <= start,
      );
    if (
      !event.midiNotes.length ||
      (release <= start && !sustainedAtStart) ||
      (end !== undefined && onset >= end)
    )
      continue;
    const common = {
      midiNotes: event.midiNotes,
      source: event.source,
      velocities: event.velocities,
    };
    result.push({
      ...common,
      id: `${event.id}:on`,
      type: 'note-on',
      atDivisions: Math.max(start, onset),
    });
    result.push({
      ...common,
      id: `${event.id}:off`,
      type: 'note-off',
      atDivisions: Math.min(end ?? Infinity, sustainedAtStart ? start + 0.0001 : release),
    });
  }
  for (const pedal of orderedPedals) {
    if (pedal.positionDivisions > start && (end === undefined || pedal.positionDivisions < end)) {
      result.push({
        id: pedal.id,
        type: 'pedal',
        atDivisions: pedal.positionDivisions,
        pedalDown: pedal.pedalDown,
      });
    }
  }
  const last = end ?? Math.max(start, ...result.map((instruction) => instruction.atDivisions));
  result.push({ id: 'boundary:end', type: 'end', atDivisions: last });
  const priority = (instruction: PlaybackInstruction) =>
    instruction.type === 'end'
      ? 4
      : instruction.type === 'pedal'
        ? 0
        : instruction.type === 'note-off'
          ? 1
          : 2;
  return result.sort((a, b) => a.atDivisions - b.atDivisions || priority(a) - priority(b));
}
